import React from 'react';
import { TextFieldProps } from '../../../types/DesignSystem/TextField';
import FieldTitle from '../FieldTitle/FieldTitle';
import { Check, Copy } from '../Iconography/Icons';
import Typography from '../Typography/Typography';
import { useMergedRef } from '../useMergedRef';
import InfoTooltip from '../Tooltip/InfoTooltip';
import { FOCUS_RING } from '../focusRing';
import { CONTROL_HEIGHT, FIELD_INSET } from '../controlSize';
import { FIELD_BOX, fieldBorder, FIELD_REST, FIELD_DISABLED } from '../fieldChrome';
import { FieldMessages, fieldInvalid, messageIds } from '../fieldHelper';
import { useControllableState } from '../useControllableState';
import { useControlSize } from '../DensityProvider/DensityProvider';

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(function TextField({
  label,
  placeholder = '',
  value: valueProp,
  defaultValue,
  onChange,
  onBlur,
  maxLength,
  disabled = false,
  required = false,
  error = false,
  helperText = '',
  errorMessage,
  hideLabel = false,
  type = 'text',
  showCopyButton = false,
  onCopy,
  additionalInfo = '',
  infoMessage = '',
  className = '',
  size: sizeProp,
  labelSize,
  minValue,
  maxValue,
  decimalAllowed = true,
  id,
  name,
  'data-testid': dataTestId,
  ariaLabel,
  'aria-label': ariaLabelNative,
  'aria-describedby': describedByProp,
  title: titleProp,
  onKeyDown: onKeyDownProp,
  step: stepProp,
  ...rest
}, ref) {
  /* § 313 — the region's density decides when the caller did not. */
  const size = useControlSize(sizeProp);
  /*
    Real label association, replacing `aria-label={label}`.

    The old wiring set `aria-label` to the same string the label already displayed.
    That names the input, so it was not *broken*, but it cost two things:

    - **Clicking the label did nothing.** No `htmlFor` existed anywhere in the
      design system, and `aria-label` cannot create that link.
    - **`aria-label` overrides the visible text for assistive technology.** The
      moment a caller passed a different `ariaLabel`, the accessible name and the
      visible name diverged — a WCAG 2.5.3 (Label in Name) failure that breaks voice
      control, because "click Product code" no longer matches anything.

    `helperText` was never referenced by `aria-describedby` either, so an errored
    field announced "invalid" with no reason (WCAG 3.3.1). Both ids come from
    `useId`, so two fields on a page cannot collide.
  */
  const uid = React.useId();
  const inputId = id ?? `${uid}-input`;
  const helperId = `${uid}-helper`;
  const errorId = `${uid}-error`;
  const infoId = `${uid}-info`;
  /* § 356 — in error when told so OR when given the error text. */
  const invalid = fieldInvalid(error, errorMessage);
  /* § 357 — uncontrolled only with `defaultValue` and no `value`; otherwise exactly the
     legacy `value = ''` field. */
  const [value, setValue] = useControllableState(valueProp, defaultValue, '');

  const describedBy = [
    // A caller's own `aria-describedby` is composed with, not replaced by, ours —
    // rest-first spreading would otherwise have silently dropped it.
    describedByProp,
    ...messageIds({ helperId, errorId }, { helperText, errorMessage }),
    additionalInfo ? infoId : undefined,
  ].filter(Boolean).join(' ') || undefined;
  /*
    § 303 — the copy button SAYS it copied. It called `navigator.clipboard.writeText`
    and showed nothing: no glyph change, no announcement, and an unhandled promise (the
    clipboard API rejects in an insecure context or without permission). Now the glyph
    flips to the set's Check for COPIED_MS, the `title` reads "Copied", and a
    pre-mounted `role="status"` announces it; on failure the field's text is selected
    instead, so a manual copy is one keystroke away. The caller's `onCopy` still
    replaces the write — and gets the same feedback.
  */
  const COPIED_MS = 1500;
  const inputRef = React.useRef<HTMLInputElement>(null);
  const setInputRef = useMergedRef<HTMLInputElement>(inputRef, ref);
  /*
    § 400 — the hover readout (`title`) only where it shows what the box cannot, and never for
    a secret. It was the value, always. On a `type="password"` field that put the password in a
    tooltip and, with no hint under the field, in the accessible DESCRIPTION, so a screen reader
    spoke it aloud on focus (measured in Chromium's accessibility tree on the Connectors SFTP
    field). On every other field the value was read twice, as the value and as its description,
    though the readout exists for a value too long for its box. So on a NAMED field (a label, an
    `aria-label` or `aria-labelledby`) it is set only while the value overflows (re-checked when
    the value or the box changes), and on a password never.

    An UNNAMED field keeps it, value and all: with no label, the `title` is the only name the input
    has, and taking it away left three showcase fields nameless (axe `label`, measured) — the app
    has ~30 more. The value is a poor name, but it is not none; the fix for those is a `label`
    (with `hideLabel`) or an `aria-label`, call site by call site.
  */
  const [overflowing, setOverflowing] = React.useState(false);
  const measureOverflow = React.useCallback(() => {
    const el = inputRef.current;
    const next = el !== null && el.scrollWidth > el.clientWidth;
    setOverflowing((prev) => (prev === next ? prev : next));
  }, []);
  React.useLayoutEffect(measureOverflow, [value, measureOverflow]);
  React.useEffect(() => {
    const el = inputRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measureOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measureOverflow]);
  const named = Boolean(label) || Boolean(ariaLabelNative ?? ariaLabel) || Boolean(rest['aria-labelledby']);
  const readout = type === 'password' ? undefined : (!named || overflowing ? value : undefined);
  const [copied, setCopied] = React.useState(false);
  const copiedTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(() => () => { if (copiedTimer.current) clearTimeout(copiedTimer.current); }, []);
  const flashCopied = () => {
    setCopied(true);
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(false), COPIED_MS);
  };

  const handleCopy = async () => {
    if (onCopy) {
      onCopy();
      flashCopied();
      return;
    }
    if (!value) return;
    try {
      if (typeof navigator.clipboard?.writeText !== 'function') throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(value);
      flashCopied();
    } catch {
      inputRef.current?.select();
    }
  };

  // Composed, not replaced. `onKeyDown` is the one native handler this component
  // owns outright: the decimal guard below is part of what `decimalAllowed={false}`
  // means. Rest-first spreading protects it from being clobbered, but that would
  // also have silently dropped a caller's own `onKeyDown` — so it is destructured
  // and invoked here instead. The guard runs first, because it is the component's
  // invariant; the caller's handler then runs unless the guard consumed the event.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === 'number' && !decimalAllowed && (e.key === '.' || e.key === ',')) {
      e.preventDefault();
    }
    if (!e.defaultPrevented) onKeyDownProp?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (type === 'number' && !decimalAllowed && newValue.includes('.')) {
      return;
    }

    if (maxValue !== undefined && Number(newValue) > maxValue) {
      setValue(maxValue.toString());
      onChange?.(maxValue.toString());
      return;
    }

    setValue(newValue);
    onChange?.(newValue, e);
  };

  return (
    <div className={className}>
      <div className="tw-flex tw-items-center tw-gap-2">
        {label && (
          <FieldTitle
            label={label}
            required={required}
            /* § 355 — hidden, not removed: it stays the input's accessible name. */
            className={hideLabel ? 'tw-sr-only' : 'tw-mb-2'}
            size={labelSize}
            htmlFor={inputId}
          />
        )}
        {infoMessage && (
          <div className="tw-mb-2">
            <InfoTooltip message={infoMessage} placement="top" />
          </div>
        )}
      </div>

      <div className="tw-relative tw-flex tw-items-center">
        <input
          /* rest first — see the rule in Button.tsx. */
          {...rest}
          ref={setInputRef}
          id={inputId}
          name={name}
          data-testid={dataTestId}
          /*
            The `?? label` fallback is gone: the visible label now names the input
            through `htmlFor`, so repeating it here could only ever create a second,
            divergable name. What remains is honoured solely because a caller asked
            for it explicitly.
          */
          aria-label={ariaLabelNative ?? ariaLabel}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          /*
            `aria-required`, not the native `required` attribute. `required` was
            reaching FieldTitle only — the asterisk was decoration and nothing told
            assistive technology the field was mandatory (WCAG 3.3.2). Adding native
            `required` would ALSO switch on browser form validation and could block
            submits that work today, in a component with 36 call sites. This states
            the fact without changing behaviour.
          */
          aria-required={required || undefined}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={onBlur}
          maxLength={maxLength}
          disabled={disabled}
          min={minValue}
          max={maxValue}
          /*
            A caller's own `step` wins. It used to be swallowed: `step` arrived through
            the rest spread and this line, which comes AFTER it, overwrote it with
            `undefined`. So `step="any"` — the ISR decimal and fraction panels' way of
            saying "this field takes fractional values" — silently became the default
            step of 1, which makes 1.5 fail native constraint validation and moves the
            spinner arrows by whole numbers.
            `decimalAllowed={false}` still pins step to 1 and is not overridable, since
            that IS the guard it names.
          */
          step={type === 'number' && !decimalAllowed ? '1' : stepProp}
          /* Field chrome — border tier, hover whisper, disabled recede, focus ring —
             comes from ONE seam, `fieldChrome.ts`. The § 31 reversal (fields rest on
             `edge-strong`, the ADS two-tier split) is documented there and its numbers
             are asserted in `borderTiers.test.tsx`. */
          /* Height from the shared control grid, not from `py-2` + a line box + a border —
             that arithmetic is what made this 38px while `Button` declared 32. `py` is
             gone: a fixed height centres an input's text on its own, and on the flex
             trigger `items-center` does it. See `controlSize.ts`. */
          /* § 381 — `pr-10` with a copy button: the button sits at the right edge over the
             input, and a long value (a script URL, a key — what the button is FOR) ran under it.
             40px clears its 12px offset and 22px box with a gap. `pr-*` is emitted after
             `px-*`, so it wins on the right side only. */
          className={`tw-w-full ${CONTROL_HEIGHT[size]} ${FIELD_INSET[size]}${showCopyButton ? ' tw-pr-10' : ''} ${FIELD_BOX} ${fieldBorder({ error: invalid, disabled })} tw-text-body-primary tw-text-ink placeholder:tw-text-ink-secondary
            ${disabled ? FIELD_DISABLED : FIELD_REST}
            ${invalid ? 'focus:tw-ring-tone-danger-glyph' : ''}
          `}
          /* The default exists so a value too long for the box is still readable
             on hover; a caller who sets `title` means something more specific. § 400 — only
             while it overflows, and never a password (see `readout`). */
          title={titleProp ?? readout}
        />

        {showCopyButton && (
          <button data-testid={dataTestId ? `${dataTestId}-copy` : 'textfield-textfield-button-1'}
            type="button"
            aria-label="Copy to clipboard"
            onClick={handleCopy}
            className={`tw-absolute tw-right-3 tw-rounded-ctl tw-text-ink-tertiary hover:tw-text-ink tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift tw-p-1 ${FOCUS_RING}`}
            title={copied ? 'Copied' : 'Copy to clipboard'}
            data-copied={copied ? 'true' : undefined}
          >
            {/* currentColor, so it inherits the button's themed ink-tertiary/ink —
                the GmsIcons CopyIcon it replaces baked stroke="#6D788C" (§ 56),
                which was near-invisible in dark inside every field. § 303 — the
                set's Check, in the success glyph ink, pops in for COPIED_MS. */}
            {copied
              ? <Check size={14} aria-hidden="true" className="tw-text-tone-success-glyph motion-safe:tw-animate-mark-pop" />
              : <Copy size={14} aria-hidden="true" />}
          </button>
        )}
        {/* § 303 — the announcement lives in a region that is ALWAYS mounted while the
            copy button is: a live region has to exist before its text lands in it. */}
        {showCopyButton && (
          <span role="status" className="tw-sr-only" data-testid={dataTestId ? `${dataTestId}-copy-status` : undefined}>
            {copied ? 'Copied to clipboard' : ''}
          </span>
        )}
      </div>

      {/*
        `role="alert"` while errored, remounted on the hint→error transition — the
        reasoning lives in `fieldHelper.tsx` (§ 302), the ONE seam every field's helper
        line renders through, so a Dropdown and a TextField in the same form cannot
        disagree about whether an error is announced. § 356 — `FieldMessages` adds the
        separate `errorMessage` line; without it this is the one line it always was.
      */}
      <FieldMessages
        helperId={helperId}
        errorId={errorId}
        helperText={helperText}
        errorMessage={errorMessage}
        error={error}
        className="tw-mt-1"
        data-testid={dataTestId}
      />

      {additionalInfo && (
        <Typography
          id={infoId}
          variant="caption-metadata"
          className="tw-mt-2 tw-text-ink-tertiary"
        >
          {additionalInfo}
        </Typography>
      )}
    </div>
  );
});

export default TextField;
